import React, { useEffect, useState } from "react";
import {
  useNavigate,
  useParams,
  useLocation,
  useOutletContext,
} from "react-router-dom";
import { getSubjectsByCourseKey } from "../../../helper/apicalls/course";
import { getSubjectsInstitution } from "../../../helper/apicalls/institution/institutionCourse";
import {
  createMaterialCourseFolder,
  getMaterialFolders,
  updateMaterialFolder,
  deleteMaterialFolder,
} from "../../../helper/apicalls/materials";
import {
  getAdminMaterialFolderInInstitution,
  getMaterialFoldersInstitution,
  createMaterialFolderInstitution,
  updateMaterialFolderInstitution,
  deleteMaterialFolderInstitution,
} from "../../../helper/apicalls/institution/materials";
import CreateFolderPopup from "../../common/CreateFolderPopup";
import DeleteConfirmPopUp from "../../common/deleteConfirmPopUp";
import { AddIcon, FolderIcon } from "../../../icons";
import GlobalLoader from "../../common/GlobalLoader";
import "./../../../styles/admin/questionBank.scss";
import Cookies from "js-cookie";

const MaterialsSubjects = () => {
  const { courseKey } = useParams();
  const navigate = useNavigate();
  const { setHeaderName } = useOutletContext();
  const location = useLocation();
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [folders, setFolders] = useState([]);
  const [showCreateFolderPopup, setShowCreateFolderPopup] = useState(false);
  const [folderMenuOpenId, setFolderMenuOpenId] = useState(null);
  const [editFolder, setEditFolder] = useState(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState(null);
  const role = Cookies.get("role");
  const permissions = Cookies.get("permissions");
  const permissionsData = permissions ? JSON.parse(permissions) : [];
  const materialPermission = Array.isArray(permissionsData)
    ? permissionsData.find((p) => p?.permission_type === "material")
    : null;
  const hasWritePermission =
    role === "admin" || (materialPermission ? !!materialPermission.write : false);

  const fetchData = async () => {
    try {
      setLoading(true);

      let subjectsData,
        adminFoldersData = [],
        institutionFoldersData = [],
        foldersData = [];

      if (role === "institution" || role === "teacher") {
        subjectsData = await getSubjectsInstitution(courseKey);
        adminFoldersData = await getAdminMaterialFolderInInstitution(
          "course",
          courseKey
        );
        institutionFoldersData = await getMaterialFoldersInstitution(
          "course",
          courseKey
        );

        foldersData = [
          ...adminFoldersData.map((folder) => ({ ...folder, is_admin: true })),
          ...institutionFoldersData.map((folder) => ({
            ...folder,
            is_admin: false,
          })),
        ];
      } else {
        subjectsData = await getSubjectsByCourseKey(courseKey);
        foldersData = await getMaterialFolders("course", courseKey);
      }

      if (Array.isArray(subjectsData) && subjectsData.length > 0) {
        const extractedSubjects = subjectsData.map((item) => ({
          id: item.id,
          name: item.name,
          key: item.key,
        }));

        setSubjects(extractedSubjects);
      } else {
        setSubjects([]);
      }

      setFolders(Array.isArray(foldersData) ? foldersData : []);
    } catch (err) {
      console.error("Error fetching subjects for Materials:", err);
      setSubjects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubjectClick = (subject) => {
    if (!subject?.key) return;
    const portalBase = role === "admin" ? "/admin" : role === "teacher" ? "/teacher" : "/institution";
    navigate(`${portalBase}/materials/course/${courseKey}/subject/${subject.key}`);
  };

  const handleFolderClick = (folder) => {
    if (!folder?.id) return;
    const portalBase = role === "admin" ? "/admin" : role === "teacher" ? "/teacher" : "/institution";
    navigate(`${portalBase}/materials/folder/${folder.id}`, {
      state: {
        level: "course",
        courseKey: courseKey,
        folderName: folder.name,
        is_admin: folder.is_admin || false,
      },
    });
  };

  const handleAddFolder = () => {
    setShowCreateFolderPopup(true);
  };

  const handleFolderMenuToggle = (id, e) => {
    e.stopPropagation();
    setFolderMenuOpenId((prev) => (prev === id ? null : id));
  };

  const handleFolderEdit = (folder, e) => {
    e.stopPropagation();
    setEditFolder(folder);
  };

  const handleFolderDelete = (folder, e) => {
    e.stopPropagation();
    setFolderToDelete(folder);
    setIsConfirmDeleteOpen(true);
  };

  useEffect(() => {
    fetchData();
    setHeaderName("Materials - Subjects");
  }, [courseKey, location]);

  useEffect(() => {
    if (!folderMenuOpenId) return;

    const handleClickOutside = (event) => {
      const menus = document.querySelectorAll(
        ".folder-menu, .folder-menu-button"
      );
      for (let i = 0; i < menus.length; i += 1) {
        const el = menus[i];
        if (el && el.contains(event.target)) {
          return;
        }
      }

      setFolderMenuOpenId(null);
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [folderMenuOpenId]);

  return (
    <>
      <GlobalLoader visible={loading} text="Loading..." />
      <div className="question-bank-container">
        <div className="qb-card">
          <div className="qb-card-header">
            <span>Course Materials</span>
            <div className="qb-card-actions">
              {hasWritePermission && (
                <button
                  type="button"
                  className="qb-add-btn"
                  onClick={handleAddFolder}
                >
                  <AddIcon className="action-button-icon" />
                  <span>Add Folder</span>
                </button>
              )}
            </div>
          </div>

          <div className="folder-grid">
            {!loading &&
              subjects?.map((sub) => {
                const id = sub.id || sub.key;
                return (
                  <div
                    key={id}
                    className="folder-item"
                    onClick={() => handleSubjectClick(sub)}
                  >
                    <div className="folder-icon">
                      <FolderIcon style={{ color: "#fccc77" }} />
                    </div>
                    <div className="folder-name">{sub.name}</div>
                  </div>
                );
              })}
            {!loading &&
              folders?.map((folder) => {
                const id = folder.id || folder.key;
                return (
                  <div
                    key={id}
                    className="folder-item"
                    onClick={() => handleFolderClick(folder)}
                  >
                    <div className="folder-icon">
                      <FolderIcon style={{ color: "var(--folder-bg)" }} />
                    </div>
                    <div className="folder-name">{folder.name}</div>
                    {(role === "institution" || role === "teacher") && folder.is_admin && (
                      <div className="qb-default-badge">Default</div>
                    )}
                    {!folder.is_admin && hasWritePermission && (
                      <button
                        type="button"
                        className="folder-menu-button"
                        onClick={(e) => handleFolderMenuToggle(id, e)}
                      >
                        ⋮
                      </button>
                    )}
                    {!folder.is_admin && hasWritePermission && folderMenuOpenId === id && (
                      <div
                        className="folder-menu"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          className="folder-menu-item"
                          onClick={(e) => handleFolderEdit(folder, e)}
                        >
                          Edit
                        </div>
                        <div
                          className="folder-menu-item --danger"
                          onClick={(e) => handleFolderDelete(folder, e)}
                        >
                          Delete
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      {showCreateFolderPopup && <CreateFolderPopup
        open={showCreateFolderPopup}
        handleClose={() => setShowCreateFolderPopup(false)}
        title="course"
        contextKey={courseKey}
        mode="create"
        onSave={async (payload) => {
          try {
            setLoading(true);
            if (role === "institution" || role === "teacher") {
              await createMaterialFolderInstitution({
                title: payload.title,
                key: payload.key,
                name: payload.name,
              });
            } else {
              await createMaterialCourseFolder(
                payload.title,
                payload.key,
                payload.name
              );
            }

            const roleRefresh = Cookies.get("role");
            let adminFoldersData = [],
              institutionFoldersData = [],
              foldersData = [];
            if (roleRefresh === "institution" || roleRefresh === "teacher") {
              adminFoldersData = await getAdminMaterialFolderInInstitution(
                "course",
                courseKey
              );
              institutionFoldersData = await getMaterialFoldersInstitution(
                "course",
                courseKey
              );

              foldersData = [
                ...adminFoldersData.map((folder) => ({
                  ...folder,
                  is_admin: true,
                })),
                ...institutionFoldersData.map((folder) => ({
                  ...folder,
                  is_admin: false,
                })),
              ];
            } else {
              foldersData = await getMaterialFolders("course", courseKey);
            }
            setFolders(Array.isArray(foldersData) ? foldersData : []);
          } catch (err) {
            console.error("Error creating course materials folder:", err);
          } finally {
            setLoading(false);
          }
        }}
      />}

     {!!editFolder && <CreateFolderPopup
        open={!!editFolder}
        handleClose={() => setEditFolder(null)}
        title="course"
        contextKey={courseKey}
        mode="edit"
        initialName={editFolder?.name || ""}
        folderId={editFolder?.id}
        onSave={async (payload) => {
          try {
            if (editFolder?.is_admin) {
              console.warn("Cannot edit admin folder");
              return;
            }

            if (!payload.id) return;
            setLoading(true);
            if (role === "institution" || role === "teacher") {
              await updateMaterialFolderInstitution(payload.id, payload.name);
            } else {
              await updateMaterialFolder(payload.id, payload.name);
            }

            const roleRefresh = Cookies.get("role");
            let adminFoldersData = [],
              institutionFoldersData = [],
              foldersData = [];
            if (roleRefresh === "institution" || roleRefresh === "teacher") {
              adminFoldersData = await getAdminMaterialFolderInInstitution(
                "course",
                courseKey
              );
              institutionFoldersData = await getMaterialFoldersInstitution(
                "course",
                courseKey
              );

              foldersData = [
                ...adminFoldersData.map((folder) => ({
                  ...folder,
                  is_admin: true,
                })),
                ...institutionFoldersData.map((folder) => ({
                  ...folder,
                  is_admin: false,
                })),
              ];
            } else {
              foldersData = await getMaterialFolders("course", courseKey);
            }
            setFolders(Array.isArray(foldersData) ? foldersData : []);
          } catch (err) {
            console.error("Error updating course materials folder:", err);
          } finally {
            setLoading(false);
            setEditFolder(null);
          }
        }}
      />}

     { isConfirmDeleteOpen&&<DeleteConfirmPopUp
        isConfirmDeleteOpen={isConfirmDeleteOpen}
        setIsConfirmDeleteOpen={setIsConfirmDeleteOpen}
        title="Delete folder"
        message={`Are you sure you want to delete the folder "${
          folderToDelete?.name || ""
        }"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        handleDelete={async () => {
          if (folderToDelete?.is_admin) {
            console.warn("Cannot delete admin folder");
            setIsConfirmDeleteOpen(false);
            return;
          }

          if (!folderToDelete?.id) {
            setIsConfirmDeleteOpen(false);
            return;
          }
          try {
            setLoading(true);
            if (role === "institution" || role === "teacher") {
              await deleteMaterialFolderInstitution(folderToDelete.id);
            } else {
              await deleteMaterialFolder(folderToDelete.id);
            }

            const roleRefresh = Cookies.get("role");
            let adminFoldersData = [],
              institutionFoldersData = [],
              foldersData = [];
            if (roleRefresh === "institution" || roleRefresh === "teacher") {
              adminFoldersData = await getAdminMaterialFolderInInstitution(
                "course",
                courseKey
              );
              institutionFoldersData = await getMaterialFoldersInstitution(
                "course",
                courseKey
              );

              foldersData = [
                ...adminFoldersData.map((folder) => ({
                  ...folder,
                  is_admin: true,
                })),
                ...institutionFoldersData.map((folder) => ({
                  ...folder,
                  is_admin: false,
                })),
              ];
            } else {
              foldersData = await getMaterialFolders("course", courseKey);
            }
            setFolders(Array.isArray(foldersData) ? foldersData : []);
          } catch (err) {
            console.error("Error deleting course materials folder:", err);
          } finally {
            setLoading(false);
            setIsConfirmDeleteOpen(false);
            setFolderToDelete(null);
          }
        }}
      />}
    </>
  );
};

export default MaterialsSubjects;
